import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import Tile from '@atlaskit/tile/tile';
import { token } from '@atlaskit/tokens';

import { SmartLinkIconRenderer } from '../SmartLinkIconRenderer.tsx';
import type { SmartLinkIcon } from '../utils/smartLinkTypes.ts';

export const HIGH_ADOPTION_THRESHOLD = 30;

const i18n = defineMessages({
	highAdoptionMessageTeamworkGraph: {
		id: 'byline-connections.menu.footer.org-connected-apps.high-adoption.teamwork-graph',
		defaultMessage:
			"{percent}% of your org uses connected apps to see how their pages connect to their team's work",
		description:
			'Promotional message in the Connections byline menu footer showing the percentage of the organization that uses connected apps to see how their pages connect to their team work.',
	},
	lowAdoptionMessage: {
		id: 'byline-connections.menu.footer.org-connected-apps.low-adoption',
		defaultMessage: 'Top teams use connected apps to get richer previews instantly',
		description:
			'Generic promotional message shown in the Connections byline menu footer when no app has >=30% org adoption.',
	},
	dismissAction: {
		id: 'byline-connections.menu.footer.org-connected-apps.dismiss-action',
		defaultMessage: 'Dismiss',
		description:
			'Aria-label for the dismiss button in the org-connected-apps promo banner in the Connections byline menu footer',
	},
});

const styles = cssMap({
	footer: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
		borderBlockStartWidth: token('border.width'),
		borderBlockStartStyle: 'solid',
		borderBlockStartColor: token('color.border'),
	},
	message: {
		font: token('font.body.small'),
		color: token('color.text'),
	},
});

export interface OrgConnectedAppsPromoBannerProps {
	icon: SmartLinkIcon | undefined;
	productLabel: string;
	topAppPercent: number | undefined;
	onDismiss: () => void;
}

export const OrgConnectedAppsPromoBanner = ({
	icon,
	productLabel,
	topAppPercent,
	onDismiss,
}: OrgConnectedAppsPromoBannerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const isHighAdoption = topAppPercent !== undefined && topAppPercent >= HIGH_ADOPTION_THRESHOLD;

	const message = isHighAdoption
		? formatMessage(i18n.highAdoptionMessageTeamworkGraph, { percent: topAppPercent })
		: formatMessage(i18n.lowAdoptionMessage);

	return (
		<Box xcss={styles.footer}>
			<Inline space="space.100" alignBlock="center" spread="space-between">
				<Inline space="space.100" alignBlock="center">
					<Tile
						label={productLabel}
						size="small"
						backgroundColor="color.background.neutral"
						hasBorder
					>
						{icon ? <SmartLinkIconRenderer icon={icon} size="menu" /> : null}
					</Tile>
					<Box xcss={styles.message}>{message}</Box>
				</Inline>
				<IconButton
					appearance="subtle"
					spacing="compact"
					icon={CrossIcon}
					label={formatMessage(i18n.dismissAction)}
					onClick={onDismiss}
				/>
			</Inline>
		</Box>
	);
};
